import moment from 'moment';

/**
 * One place that decides how every chart on the dashboard looks and what it is
 * allowed to claim.
 *
 * Each tab used to declare its own colours, its own tooltip, its own axes and
 * its own idea of a series colour — so the same "users" measure was #7C3AED on
 * one tab and #8d6cfa on another, and no two charts shared a grid.
 */

export const PALETTE = {
  brand: '#8d6cfa',
  brandDeep: '#5b3fc4',
  ink: '#16054e',
  muted: '#6b6684',
  line: '#ece9f5',
  grid: '#eeecf6',
  sunken: '#f7f6fc',
  good: '#0f8a6a',
  warn: '#b4530a',
  bad: '#c62828',
};

/**
 * Categorical series colours.
 *
 * Ordered so that neighbours stay distinguishable, and kept separate from the
 * semantic good/warn/bad set — a category is not a judgement.
 */
export const SERIES = [
  '#8d6cfa',
  '#0f8a6a',
  '#d97706',
  '#2f6bd4',
  '#c94060',
  '#0e9aa7',
  '#7a4fb5',
  '#8a8f2f',
];

export const seriesColor = index => SERIES[index % SERIES.length];

/** A translucent version of a series colour, for area fills. */
export const seriesWash = (index, alpha = 0.14) => {
  const hex = seriesColor(index).replace('#', '');
  const to = offset => parseInt(hex.slice(offset, offset + 2), 16);
  return `rgba(${to(0)}, ${to(2)}, ${to(4)}, ${alpha})`;
};

export const compactNumber = value =>
  value == null
    ? '—'
    : new Intl.NumberFormat('en-US', {
        notation: Math.abs(value) >= 100000 ? 'compact' : 'standard',
        maximumFractionDigits: 1,
      }).format(value);

/** An average: one decimal only while the decimal still carries information. */
export const averageNumber = value => {
  if (value == null) return '—';
  return value >= 100
    ? new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value)
    : value.toFixed(1);
};

export const plainNumber = value =>
  value == null ? '—' : new Intl.NumberFormat('en-US').format(value);

/**
 * Money. Two decimal places or none — never the three that
 * `Intl.NumberFormat`'s default maximum produced on figures like 1157.711.
 */
export const moneyNumber = value => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Expands a sparse daily series into a full calendar month.
 *
 * The growth endpoints return ONLY the days that had activity. Plotted as-is on
 * a categorical axis, 1 August and 12 August end up adjacent and equally
 * spaced, so a week with no signups is drawn as a flat line holding at the
 * previous value instead of a drop to zero. The chart was describing a trend
 * the data does not contain.
 *
 * Filling the gaps with explicit zeros makes the horizontal axis a real
 * calendar again, so distance on screen means elapsed time.
 *
 * The current month stops at today rather than running to the 31st — trailing
 * zeros for days that have not happened yet would read as a collapse.
 */
export const fillCalendarDays = (points, month, year) => {
  const byDay = Object.create(null);
  (points ?? []).forEach(point => {
    if (!point?.date) return;
    byDay[moment(point.date).format('YYYY-MM-DD')] = Number(point.amount) || 0;
  });

  const start = moment(`${year}-${String(month).padStart(2, '0')}-01`);
  if (!start.isValid()) return points ?? [];

  const today = moment();
  const lastDay = start.isSame(today, 'month')
    ? today.date()
    : start.daysInMonth();

  const filled = [];
  for (let day = 1; day <= lastDay; day += 1) {
    const date = start.clone().date(day);
    const key = date.format('YYYY-MM-DD');
    filled.push({
      date: date.toISOString(),
      amount: byDay[key] ?? 0,
      label: date.format('D MMM'),
      recorded: key in byDay,
    });
  }
  return filled;
};

/**
 * The same gap-filling, one level up.
 *
 * `getUsersSignupChartData` has the identical shape problem as the daily
 * endpoint: 44 monthly points spanning roughly fifty months, so August 2022 is
 * simply absent and September is drawn next to July. Filling between the first
 * and last recorded period keeps the axis proportional to elapsed time.
 */
const PERIOD_FORMAT = {
  week: '[Wk] WW GGGG',
  month: 'MMM YYYY',
  year: 'YYYY',
};

export const fillPeriods = (points, unit) => {
  const recorded = (points ?? [])
    .filter(point => point?.date)
    .map(point => ({
      at: moment(point.date).startOf(unit),
      amount: Number(point.amount) || 0,
    }))
    .sort((a, b) => a.at.valueOf() - b.at.valueOf());

  if (!recorded.length) return [];

  const byKey = Object.create(null);
  recorded.forEach(entry => {
    const key = entry.at.format('YYYY-MM-DD');
    byKey[key] = (byKey[key] ?? 0) + entry.amount;
  });

  const cursor = recorded[0].at.clone();
  const last = recorded[recorded.length - 1].at;
  const filled = [];

  // A guard rather than a limit: 600 weeks is over eleven years, well past any
  // range this data covers, and it stops a bad date from spinning forever.
  while (cursor.isSameOrBefore(last, unit) && filled.length < 600) {
    const key = cursor.format('YYYY-MM-DD');
    filled.push({
      date: cursor.toISOString(),
      amount: byKey[key] ?? 0,
      label: cursor.format(PERIOD_FORMAT[unit] ?? 'D MMM YYYY'),
      recorded: key in byKey,
    });
    cursor.add(1, unit);
  }
  return filled;
};

/** Headline figures for a filled daily series. */
export const summariseSeries = series => {
  const values = (series ?? []).map(point => Number(point.amount) || 0);
  if (!values.length)
    return {total: 0, peak: null, average: 0, activeDays: 0, days: 0};

  const total = values.reduce((sum, value) => sum + value, 0);
  const peakIndex = values.reduce(
    (best, value, index) => (value > values[best] ? index : best),
    0,
  );

  return {
    total,
    peak: values[peakIndex] > 0 ? series[peakIndex] : null,
    average: total / values.length,
    activeDays: values.filter(value => value > 0).length,
    days: values.length,
  };
};

/**
 * What a month is compared against.
 *
 * A finished month is compared with the whole month before it. The month in
 * progress is compared with the same days of the month before — on the 1st of
 * October, with the 1st of September — because one day against a full month
 * reads as "down 100%" every time a month begins.
 *
 * `previousPoints` are the previous month's `{date, amount}` days; `today` is
 * injectable for tests. Returns `{base, monthTotal, inProgress, throughDay}`:
 * `base` is the figure to compare with, `monthTotal` the previous month whole.
 */
export const comparisonBase = (
  previousPoints,
  month,
  year,
  today = moment(),
) => {
  if (!previousPoints) return null;
  const start = moment(`${year}-${String(month).padStart(2, '0')}-01`);
  const inProgress = start.isSame(today, 'month');
  const throughDay = inProgress ? today.date() : null;
  let base = 0;
  let monthTotal = 0;
  previousPoints.forEach(point => {
    const amount = Number(point?.amount) || 0;
    monthTotal += amount;
    if (!inProgress || moment(point?.date).date() <= throughDay) base += amount;
  });
  return {base, monthTotal, inProgress, throughDay};
};

/** Percentage change against a previous period, or null when there is no base. */
export const changeAgainst = (current, previous) => {
  if (previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

/**
 * Base Chart.js options every chart starts from.
 *
 * `integer` matters more than it looks: these series count people, and the
 * default tick generator produced 0.5 and 2.5 gridlines on a chart whose
 * highest value was 3 — half a person.
 */
export const baseOptions = ({integer = false, valueLabel = 'Value'} = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {mode: 'index', intersect: false},
  animation: {duration: 260},
  plugins: {
    legend: {display: false},
    tooltip: {
      backgroundColor: '#ffffff',
      titleColor: PALETTE.ink,
      bodyColor: PALETTE.ink,
      borderColor: PALETTE.line,
      borderWidth: 1,
      cornerRadius: 10,
      padding: 12,
      titleFont: {size: 12, weight: '600'},
      bodyFont: {size: 13},
      displayColors: true,
      boxWidth: 8,
      boxHeight: 8,
      usePointStyle: true,
      callbacks: {
        label: context =>
          ` ${context.dataset.label || valueLabel}: ${plainNumber(
            context.parsed.y ?? context.parsed,
          )}`,
      },
    },
  },
  scales: {
    x: {
      grid: {display: false},
      border: {color: PALETTE.line},
      ticks: {
        color: PALETTE.muted,
        font: {size: 11},
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 10,
        padding: 8,
      },
    },
    y: {
      beginAtZero: true,
      grid: {color: PALETTE.grid, drawTicks: false},
      border: {display: false},
      ticks: {
        color: PALETTE.muted,
        font: {size: 11},
        padding: 10,
        precision: integer ? 0 : undefined,
        callback: value =>
          integer && !Number.isInteger(value) ? null : compactNumber(value),
      },
    },
  },
});
